import { useState } from "react";
import { Plus, Search, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, Empty } from "@/components/atlas/Shared";
import { GoldButton, NativeSelect } from "@/components/atlas/Fields";
import { PositionSelect } from "@/components/events/PositionsEditor";
import { EmployeeDialog } from "@/components/employees/EmployeeDialog";
import { EmployeeSheet } from "@/components/employees/EmployeeSheet";
import { DeleteEmployeesDialog } from "@/components/employees/DeleteEmployeesDialog";
import { useFetch, usePositions } from "@/lib/positions";
import { useLang } from "@/lib/i18n";

export default function Employees() {
  const { t } = useLang();
  const { label } = usePositions();
  const [f, setF] = useState({ q: "", position: "", status: "" });
  const { data, reload } = useFetch("/employees", f);
  const [dialog, setDialog] = useState({ open: false, employee: null });
  const [viewing, setViewing] = useState(null);
  const [selected, setSelected] = useState([]);
  const [toDelete, setToDelete] = useState([]);
  const inactive = (data || []).filter((e) => e.status === "inactive");
  const sel = inactive.filter((e) => selected.includes(e.id));
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const afterDelete = () => {
    setToDelete([]);
    setSelected([]);
    setViewing(null);
    reload();
  };

  return (
    <div>
      <PageHeader eyebrow="Atlas" title={t("emp.title")} sub={data ? `${data.length} ${t("emp.count")}` : ""}
        actions={<GoldButton data-testid="new-employee-btn" onClick={() => setDialog({ open: true, employee: null })}><Plus className="w-4 h-4" />{t("emp.new")}</GoldButton>} />
      <div className="card-lux p-4 mb-6 grid md:grid-cols-[2fr_1fr_1fr] gap-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-neutral-400" />
          <Input data-testid="employees-search" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder={t("c.search")} className="pl-9 h-10 rounded-none" />
        </div>
        <PositionSelect allowEmpty value={f.position} onChange={(v) => setF({ ...f, position: v })} testid="employees-filter-position" />
        <NativeSelect testid="employees-filter-status" value={f.status} onChange={(v) => setF({ ...f, status: v })}>
          <option value="">{t("c.status")} · {t("c.all")}</option>
          <option value="active">{t("emp.active")}</option>
          <option value="inactive">{t("emp.inactive")}</option>
        </NativeSelect>
      </div>
      {data && data.length === 0 && <Empty text={t("c.none")} />}
      {sel.length > 0 && (
        <div className="card-lux p-4 mb-4 flex flex-wrap items-center justify-between gap-3 rise" data-testid="employees-bulk-bar">
          <span className="text-sm"><span className="mono text-[#D9B872]" data-testid="employees-selected-count">{sel.length}</span> {t("emp.selected")}</span>
          <button data-testid="employees-bulk-delete-btn" onClick={() => setToDelete(sel)} className="press inline-flex items-center gap-2 h-10 px-4 rounded-full bg-red-600 text-white text-sm font-semibold hover:bg-red-500"><Trash2 className="w-4 h-4" />{t("emp.deleteSelected")}</button>
        </div>
      )}
      <div className="card-lux overflow-x-auto" data-testid="employees-table">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-black/[0.06] text-left">
              <th className="px-4 py-3 w-10">{inactive.length > 0 && <Checkbox data-testid="employees-select-all-inactive" aria-label={t("emp.selectInactive")} checked={sel.length === inactive.length} onCheckedChange={(v) => setSelected(v ? inactive.map((e) => e.id) : [])} />}</th>
              {[t("c.name"), t("emp.primary"), t("emp.qualified"), t("c.phone"), t("c.status")].map((h) => <th key={h} className="eyebrow font-semibold px-5 py-3">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {(data || []).map((e) => (
              <tr key={e.id} onClick={() => setViewing(e.id)} data-testid={`employee-row-${e.id}`} className="border-b border-black/[0.04] last:border-0 cursor-pointer hover:bg-[#C5A059]/[0.05]">
                <td className="px-4 py-4" onClick={(ev) => ev.stopPropagation()}>{e.status === "inactive" && <Checkbox data-testid={`employee-select-${e.id}`} checked={selected.includes(e.id)} onCheckedChange={() => toggle(e.id)} />}</td>
                <td className="px-5 py-4">
                  <div className="font-medium">{e.name}</div>
                  <div className="text-xs text-neutral-500">{e.email}{e.role === "admin" && <span className="ml-2 text-[#8a6d2f] uppercase tracking-wider text-[10px]" data-testid={`employee-admin-badge-${e.id}`}>{t("emp.role.admin")}</span>}</div>
                </td>
                <td className="px-5 py-4">{label(e.primary_position)}</td>
                <td className="px-5 py-4">
                  <div className="flex flex-wrap gap-1">{e.positions.filter((p) => p !== e.primary_position).map((p) => <span key={p} className="text-[10px] border border-black/10 px-1.5 py-0.5">{label(p)}</span>)}</div>
                </td>
                <td className="px-5 py-4 mono text-xs">{e.phone}</td>
                <td className="px-5 py-4">
                  <span className={`text-[11px] uppercase tracking-wider ${e.status === "active" ? "text-emerald-700" : "text-neutral-400"}`}>{e.status === "active" ? t("emp.active") : t("emp.inactive")}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <EmployeeSheet employeeId={viewing} onClose={() => setViewing(null)} onEdit={(e) => setDialog({ open: true, employee: e })} onDelete={(e) => setToDelete([e])} />
      <DeleteEmployeesDialog people={toDelete} onClose={() => setToDelete([])} onDeleted={afterDelete} />
      <EmployeeDialog open={dialog.open} employee={dialog.employee} onClose={() => setDialog({ open: false, employee: null })}
        onSaved={() => { reload(); if (viewing) { const v = viewing; setViewing(null); setTimeout(() => setViewing(v), 0); } }} />
    </div>
  );
}
